import { html, unsafeCSS } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_GENERAL_COMMUNICATION } from '../../../../../../../webcomponents.constants';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  GeneralCommunicationFormValue,
  GeneralCommunicationSearchableKeywords,
} from '../../../../utils/uiSettings/general/general.types';
import {
  getGeneralCommunicationDefaultValue,
  getGeneralCommunicationSearchableKeywords,
} from '../../../../utils/uiSettings/general/general.values';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import _ from 'lodash';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { CommunicationProxyType, EcsAssignment } from '@nirvana/shared/dtos/types/policies/common';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { GzEcsAssignmentGridWebComponent } from '../gridComponents/endpointCommunicationAssignment/GzEcsAssignmentGridWebComponent';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { classMap } from 'lit/directives/class-map.js';

/**
 * @summary Type here the description of this component
 * @tag communication
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_POLICY_PANEL_GENERAL_COMMUNICATION)
export class GzGeneralCommunicationWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  GeneralCommunicationFormValue,
  GeneralCommunicationSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.GENERAL,
    sectionId: SectionId.COMMUNICATION,
  };

  protected readonly isRemoteShellHidden = !Application.Main.checkIfUserCanUseRemoteShellForCurrentCompany();
  protected readonly isRemoteShellDisabled = !Application.Main.product.isCloud();

  protected searchableKeywords = getGeneralCommunicationSearchableKeywords();

  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }
  @query('#relay-communication-form-field')
  private relayCommunicationFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#cloud-communication-form-field')
  private cloudCommunicationFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#remote-shell-form-field')
  private remoteShellFormField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#ecs-assignment-form-field')
  private ecsAssignmentFormField: GzFormFieldWebComponent<GzEcsAssignmentGridWebComponent, EcsAssignment[]>;
  protected createFormGroup(): void {
    this.formGroup = FormGroup.build({
      uiSettings: {
        general: {
          communication: {
            ecsAssignments: this.ecsAssignmentFormField,
            cloudServicesProxy: {
              profile: this.cloudCommunicationFormField,
            },
            ecsProxy: {
              profile: this.relayCommunicationFormField,
            },
            remoteShell: { enabled: this.remoteShellFormField },
          },
        },
      },
    });
  }

  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }
    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'general', 'communication']);
    }
    return value;
  }

  protected getValueFromManager(): GeneralCommunicationFormValue {
    const defaultValue = {
      uiSettings: {
        [ModuleId.GENERAL]: {
          [SectionId.COMMUNICATION]: getGeneralCommunicationDefaultValue(),
        },
      },
    };
    const value = {
      uiSettings: {
        [ModuleId.GENERAL]: {
          [SectionId.COMMUNICATION]: PolicyPanelManager.getInstance().getPolicy('uiSettings.general.communication'),
        },
      },
    };
    return deepmerge(defaultValue, value);
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <span>
      <gz-title
        searchable-label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.Communication].label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.TITLE}"
      >
        ${this.searchableKeywords[GeneralCommunicationSearchableKeywords.Communication].translatedLabel}
      </gz-title></span
    >`;
  }

  /**
   * Render specific info text.
   */
  protected renderInfoText() {
    return html``;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`<div id="general-communication-content">
      <gz-title
        searchable-label="${this.searchableKeywords[
          GeneralCommunicationSearchableKeywords.EndpointCommunicationAssignment
        ].label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.HEADING_4}"
        class="form-subtitle"
      >
        ${this.searchableKeywords[GeneralCommunicationSearchableKeywords.EndpointCommunicationAssignment]
          .translatedLabel}
      </gz-title>
      <gz-text class="infoText mt-1" fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}"
        >${translateFn(
          'Assign one or more Relays or Communication Servers to the target endpoints.'
        )}</gz-text
      >
      <gz-form-field id="ecs-assignment-form-field">
        <gz-policy-panel-general-endpoint-communication-assignment-grid
          gz-form-input
          class="removeGridPadding hideButtonsOutsideGrid"
        >
        </gz-policy-panel-general-endpoint-communication-assignment-grid>
      </gz-form-field>
      <gz-title
        searchable-label="${this.searchableKeywords[
          GeneralCommunicationSearchableKeywords.CommunicationBetweenEndpointsAndRelays
        ].label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.HEADING_4}"
        class="form-subtitle"
      >
        ${this.searchableKeywords[GeneralCommunicationSearchableKeywords.CommunicationBetweenEndpointsAndRelays]
          .translatedLabel}
      </gz-title>
      <gz-form-field id="relay-communication-form-field">
        <gz-radio-group gz-form-input>
          <div class="radio">
            <gz-radio
              class="mt-2"
              .value="${CommunicationProxyType.KEEP_CURRENT_SETTINGS}"
              label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.UsePreviousSettings]
                .translatedLabel}"
              searchable-label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.UsePreviousSettings]
                .label}"
            >
            </gz-radio>
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="form-field-info-text mb-2"
              >${translateFn(
                'Select this option to preserve the proxy settings already configured via the installation package or a previous policy.'
              )}</gz-text
            >
          </div>
          <gz-radio
            class="mt-2"
            .value="${CommunicationProxyType.ENABLE_PROXY}"
            label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.UseProxyDefinedInSettings]
              .translatedLabel}"
            searchable-label="${this.searchableKeywords[
              GeneralCommunicationSearchableKeywords.UseProxyDefinedInSettings
            ].label}"
          >
          </gz-radio>
          <gz-radio
            class="mt-2"
            .value="${CommunicationProxyType.DISABLE_PROXY}"
            label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.DoNotUseProxy].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.DoNotUseProxy].label}"
          >
          </gz-radio>
        </gz-radio-group>
      </gz-form-field>
      <gz-title
        searchable-label="${this.searchableKeywords[
          GeneralCommunicationSearchableKeywords.CommunicationBetweenEndpointsAndCloudServices
        ].label}"
        fontWeight="${FontWeights.MEDIUM}"
        fontSize="${FontSizes.HEADING_4}"
        class="form-subtitle"
      >
        ${this.searchableKeywords[GeneralCommunicationSearchableKeywords.CommunicationBetweenEndpointsAndCloudServices]
          .translatedLabel}
      </gz-title>
      <gz-form-field id="cloud-communication-form-field">
        <gz-radio-group gz-form-input>
          <div class="radio">
            <gz-radio
              class="mt-2"
              .value="${CommunicationProxyType.KEEP_CURRENT_SETTINGS}"
              label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.UsePreviousSettings]
                .translatedLabel}"
              searchable-label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.UsePreviousSettings]
                .label}"
            >
            </gz-radio>
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="form-field-info-text mb-2"
              >${translateFn(
                'Select this option to preserve the proxy settings already configured via the installation package or a previous policy.'
              )}</gz-text
            >
          </div>
          <gz-radio
            class="mt-2"
            .value="${CommunicationProxyType.ENABLE_PROXY}"
            label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.UseProxyDefinedInSettings]
              .translatedLabel}"
            searchable-label="${this.searchableKeywords[
              GeneralCommunicationSearchableKeywords.UseProxyDefinedInSettings
            ].label}"
          >
          </gz-radio>
          <gz-radio
            class="mt-2"
            .value="${CommunicationProxyType.AUTO_DETECT_PROXY}"
            label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.AutoDetectProxySettings]
              .translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.AutoDetectProxySettings]
              .label}"
          >
          </gz-radio>
          <gz-radio
            class="mt-2"
            .value="${CommunicationProxyType.DISABLE_PROXY}"
            label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.DoNotUseProxy].translatedLabel}"
            searchable-label="${this.searchableKeywords[GeneralCommunicationSearchableKeywords.DoNotUseProxy].label}"
          >
          </gz-radio>
        </gz-radio-group>
      </gz-form-field>

      <gz-form-field
        id="remote-shell-form-field"
        ?disabled="${this.isRemoteShellDisabled}"
        class="${classMap({ hidden: this.isRemoteShellHidden })} mt-4"
      >
        <gz-toggle
          class="content-title-toggle"
          searchable-label="${this.searchableKeywords[
            GeneralCommunicationSearchableKeywords.RemoteShellConnectionWithEndpoint
          ].label}"
          gz-form-input
          class="form-subtitle"
        >
          <span
            >${this.searchableKeywords[GeneralCommunicationSearchableKeywords.RemoteShellConnectionWithEndpoint]
              .translatedLabel}</span
          >
        </gz-toggle>
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="400" class="form-field-info-text mt-1 mb-2"
          >${translateFn(
            'When enabled, this setting allows remote shell connections to endpoints in your environment.'
          )}</gz-text
        >
      </gz-form-field>
    </div>`;
  }

  protected setFormData(): void {
    if (!this.valueT) {
      return;
    }
    this.formGroup.value = {
      uiSettings: {
        general: {
          communication: this.valueT.uiSettings.general.communication,
        },
      },
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_GENERAL_COMMUNICATION: GzGeneralCommunicationWebComponent;
  }
}
